<template>
  <WizardDialog
    :visible="visible"
    :phase="wizard.phase.value"
    :phases="PHASES"
    :titles="titles"
    :subtitles="subtitles"
    :locked="submitting || locked"
    width="760px"
    :close-label="t('billing.buyCredits.cancel')"
    @update:visible="onVisibleChange"
    @close="onClose"
  >
    <!-- Phase 1 : Selection -->
    <template #select>
      <div class="buy-credits__phase-body">
        <WalletTopUpSelection
          :amount="amount"
          :country="country"
          :currency="currency"
          :disabled="submitting"
          @update:amount="amount = $event"
          @update:country="country = $event"
        />
      </div>
      <div class="buy-credits__foot buy-credits__foot--end">
        <WizardActionButton
          variant="next"
          :label="t('billing.buyCredits.continue')"
          trailing-icon="fa-regular fa-arrow-right"
          :disabled="!canContinue"
          @click="wizard.next"
        />
      </div>
    </template>

    <!-- Phase 2 : Review + Payment -->
    <template #review>
      <div class="buy-credits__phase-body">
        <WalletTopUpReview
          :amount="amount"
          :country="country"
          :currency="currency"
          :payment-mode="paymentMode"
          :payment-method-id="paymentMethodId"
          :default-card="defaultCard"
          :all-cards="allCards"
          :payment-error="paymentError"
          :dropin-submit-handler="onDropinSubmit"
          :dropin-details-handler="onDropinDetails"
          :cta-label="hasDefaultCard ? t('billing.buyCredits.pay', { amount: formatAmount(amount) }) : ''"
          :cta-submitting="submitting"
          @update:paymentMode="paymentMode = $event"
          @update:paymentMethodId="paymentMethodId = $event"
          @dropin-success="onDropinSuccess"
          @dropin-error="onDropinError"
          @cta-click="onConfirm"
        />
      </div>
      <div class="buy-credits__foot">
        <WizardBackButton
          :label="t('billing.buyCredits.back')"
          :disabled="submitting"
          @click="wizard.back"
        />
      </div>
    </template>
  </WizardDialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import WizardDialog from '@/components/billing/shared/WizardDialog.vue';
import WizardBackButton from '@/components/billing/shared/WizardBackButton.vue';
import WizardActionButton from '@/components/billing/shared/WizardActionButton.vue';
import WalletTopUpSelection from '@/components/billing/dialogs/WalletTopUpSelection.vue';
import WalletTopUpReview from '@/components/billing/dialogs/WalletTopUpReview.vue';
import {
  ActionResult,
  confirmPlan,
  walletTopUp,
} from '@/composables/billing/billingApi';
import { useStructuralReload } from '@/composables/billing/useStructuralReload';
import { useWizard } from '@/composables/billing/useWizard';
import { useSmsCountries } from '@/composables/billing/useSmsCountries';
import { formatBillingAmount } from '@/composables/billing/formatAmount';

const PHASES = ['select', 'review'] as const;

type PaymentMode = 'existing' | 'new';

/**
 * One-shoot wallet top-up modal. Two-phase wizard (select → review) like
 * {@link ChangePlanDialog}, but rendered in a **compact** Dialog matching
 * {@link RepayInvoiceDialog}'s footprint (760px, not full-screen). The
 * buy-credits decision is lighter than a plan change, so the full-screen
 * canvas is unnecessary — but the two-step shape still helps the user
 * commit to a quantity before seeing the payment block.
 */
export default defineComponent({
  name: 'BuyCreditsDialog',

  components: {
    WizardDialog,
    WizardBackButton,
    WizardActionButton,
    WalletTopUpSelection,
    WalletTopUpReview,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:visible', 'bought'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const store = useStore();
    const handleReload = useStructuralReload();
    const wizard = useWizard(PHASES);
    const sms = useSmsCountries('oneShoot');

    // ─── State ───────────────────────────────────────────────────────────
    const amount = ref<number>(25);
    // Default country = shop's home country (FR / GB / US / ...), falling
    // back to FR. Read at setup time from the cached overview ; the dropdown
    // remains user-driven afterwards.
    const initialCountry = store.state.billing.overview?.shop?.country ?? 'FR';
    const country = ref<string>(initialCountry);
    const paymentMode = ref<PaymentMode>('existing');
    // null = pay with default card (back falls back to getDefaultOrOneToken).
    // string = pay with this specific saved card (PaymentBlock select view).
    const paymentMethodId = ref<string | null>(null);
    const submitting = ref(false);
    const locked = ref(false);
    const paymentError = ref<string | null>(null);

    // ─── Derived state ───────────────────────────────────────────────────
    const overview = computed(() => store.state.billing.overview);
    const allCards = computed(() => overview.value?.paymentMethods?.items ?? []);
    const defaultCard = computed(() => (
      allCards.value.find((p: any) => p.isDefault) ?? null
    ));
    const hasDefaultCard = computed(() => defaultCard.value !== null);
    const currency = computed<'EUR' | 'USD'>(
      () => (overview.value?.shop?.currency === 'USD' ? 'USD' : 'EUR'),
    );

    watch(country, (v) => { sms.selectedCountry.value = v; }, { immediate: true });

    const estimatedSmsCount = computed(() => sms.estimatedSmsCount(amount.value));

    const canContinue = computed(() => (
      amount.value > 0 && !!sms.selectedCountryData.value
    ));

    // ─── Titles / subtitles ──────────────────────────────────────────────
    const titles = computed(() => ({
      select: t('billing.buyCredits.titleSelect'),
      review: t('billing.buyCredits.titleReview'),
    }));
    const subtitles = computed(() => ({
      select: t('billing.buyCredits.subtitleSelect'),
      review: t('billing.buyCredits.subtitleReview'),
    }));

    // ─── Actions ─────────────────────────────────────────────────────────
    const onConfirm = async () => {
      paymentError.value = null;
      submitting.value = true;
      try {
        const result = await walletTopUp({
          country: country.value,
          smsCount: estimatedSmsCount.value,
          // When the user picked a non-default saved card in PaymentBlock,
          // forward its id to the back so it uses the right token instead
          // of falling back to `getDefaultOrOneToken`.
          ...(paymentMethodId.value
            ? { paymentMethodId: paymentMethodId.value }
            : {}),
        });
        await handleReload(result, {
          onFailure: (err) => {
            paymentError.value = err;
          },
          onShopifyApproval: (confirmationUrl) => {
            locked.value = true;
            window.location.href = confirmationUrl;
          },
          onActionRequired: () => {
            paymentError.value = t('billing.payment.actionRequired');
          },
        });
        if (result.status === 'completed') {
          emit('bought');
          emit('update:visible', false);
        }
      } finally {
        submitting.value = false;
      }
    };

    // ─── Drop-in (new card) handlers ─────────────────────────────────────
    const onDropinSubmit = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => walletTopUp({
      country: country.value,
      smsCount: estimatedSmsCount.value,
      adyenStateData,
    });

    const onDropinDetails = (
      adyenStateData: Record<string, any>,
    ): Promise<ActionResult> => confirmPlan({ adyenStateData });

    const onDropinSuccess = async () => {
      await store.dispatch('billing/refetchOverview');
      emit('bought');
      emit('update:visible', false);
    };

    const onDropinError = (err: string) => {
      paymentError.value = err;
    };

    // ─── Lifecycle ───────────────────────────────────────────────────────
    const reset = () => {
      wizard.reset();
      amount.value = 25;
      country.value = store.state.billing.overview?.shop?.country ?? 'FR';
      paymentMode.value = 'existing';
      paymentMethodId.value = null;
      paymentError.value = null;
      locked.value = false;
    };

    const onVisibleChange = (v: boolean) => { if (!v) onClose(); };

    const onClose = () => {
      if (submitting.value || locked.value) return;
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    watch(() => props.visible, (v) => { if (v) reset(); });
    watch(hasDefaultCard, (has) => {
      paymentMode.value = has ? 'existing' : 'new';
    }, { immediate: true });

    const formatAmount = (n: number) => formatBillingAmount(n, currency.value);

    return {
      t,
      PHASES,
      wizard,
      titles,
      subtitles,
      amount,
      country,
      paymentMode,
      paymentMethodId,
      submitting,
      locked,
      paymentError,
      defaultCard,
      allCards,
      currency,
      hasDefaultCard,
      canContinue,
      onConfirm,
      onDropinSubmit,
      onDropinDetails,
      onDropinSuccess,
      onDropinError,
      onVisibleChange,
      onClose,
      formatAmount,
    };
  },
});
</script>

<style lang="scss" scoped>
.buy-credits {
  // Compact wizard mode — no fullScreen, the dialog sizes to its content
  // (header + progress + active phase). The phase body scrolls if it
  // overflows the viewport (PrimeVue handles that via .p-dialog-mask).
  &__phase-body {
    padding-bottom: 16px;
  }

  &__foot {
    display: flex;
    align-items: center;
    padding: 16px 0 8px;
    background: #fff;
    border-top: 1px solid #f1f5f9;

    &--end {
      justify-content: flex-end;
    }
  }
}
</style>
